/**
 * Admin Messages Section - Real-Time Communication Hub
 * Features:
 * - WebSocket-based instant messaging with carriers
 * - Pusher push notifications
 * - Load offer system (send with screenshot, track status)
 * - Document signing requests
 * - File sharing (images, PDFs)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import { 
  MessageCircle, Send, Image, FileText, Truck, Search,
  Phone, Mail, User, Clock, CheckCircle, X, AlertCircle,
  Upload, Package, ChevronRight, Paperclip, Bell, Loader2,
  Circle, Check, CheckCheck, Plus, Camera, File, FileSignature, 
  PenTool, Download
} from 'lucide-react';
import { subscribeToAdminNotifications, unsubscribeFromChannel, NotificationEvents } from '../../../services/pusherService';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Typing indicator component
const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-3 py-2">
    <div className="flex gap-1">
      <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
      <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
      <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
    </div>
    <span className="text-white/50 text-xs ml-2">typing...</span>
  </div>
);

const AdminMessagesSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('messages'); // messages, load-offers
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadOffers, setLoadOffers] = useState([]);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [showLoadOfferModal, setShowLoadOfferModal] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [ws, setWs] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false); // Track if other party is typing
  const [typingUser, setTypingUser] = useState(null); // Who is typing
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Connect WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/messaging/ws/admin/admin`;
    
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log('WebSocket connected');
      setWs(websocket);
    };
    
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWebSocketMessage(data);
    };
    
    websocket.onclose = () => {
      console.log('WebSocket disconnected');
      // Attempt reconnection after 3 seconds
      setTimeout(() => {
        if (websocket.readyState === WebSocket.CLOSED) {
          window.location.reload();
        }
      }, 3000);
    };
    
    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, [navigate]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'new_message':
        // Add message to current conversation if it matches
        if (selectedConversation?.id === data.message.conversation_id) {
          setMessages(prev => [...prev, data.message]);
          // Clear typing indicator when message received
          setIsTyping(false);
          setTypingUser(null);
        }
        // Refresh conversations to update last message
        fetchConversations();
        // Show notification
        if (Notification.permission === 'granted') {
          new Notification('New Message', {
            body: `${data.message.sender_name}: ${data.message.content}`,
            icon: '/logo192.png'
          });
        }
        break;
      case 'load_offer_response':
        // Update load offer status
        setLoadOffers(prev => prev.map(offer => 
          offer.id === data.offer.id ? data.offer : offer
        ));
        break;
      case 'document_signed':
        // Handle signed document notification
        console.log('Document signed:', data.request);
        break;
      case 'typing':
        // Handle typing indicator from other party
        if (selectedConversation?.id === data.conversation_id) {
          setIsTyping(true);
          setTypingUser(data.sender_id);
          // Auto-clear typing after 3 seconds of no updates
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
            setTypingUser(null);
          }, 3000);
        }
        break;
      case 'read_receipt':
        // Update read status for messages
        if (data.message_ids && data.message_ids.length > 0) {
          setMessages(prev => prev.map(msg => 
            data.message_ids.includes(msg.id) ? { ...msg, read: true } : msg
          ));
        }
        break;
      default:
        break;
    }
  }, [selectedConversation]);

  // Fetch data on mount
  useEffect(() => {
    fetchConversations();
    fetchLoadOffers();
    fetchCarriers();
    fetchUnreadCount();
    fetchSignatureRequests();
    
    // Request notification permission (check if API exists first)
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Subscribe to Pusher notifications
    let pusherChannel = null;
    const setupPusher = async () => {
      pusherChannel = await subscribeToAdminNotifications({
        [NotificationEvents.NEW_MESSAGE]: (data) => {
          fetchConversations();
          fetchUnreadCount();
        },
        [NotificationEvents.LOAD_OFFER_RESPONSE]: (data) => {
          fetchLoadOffers();
        },
        [NotificationEvents.DOCUMENT_SIGNED]: (data) => {
          fetchSignatureRequests();
        }
      });
    };
    setupPusher();
    
    setLoading(false);
    
    // Cleanup on unmount
    return () => {
      if (pusherChannel) {
        unsubscribeFromChannel('admin-notifications');
      }
    };
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/conversations/admin?user_type=admin`);
      const data = await res.json();
      setConversations(data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/conversations/${conversationId}/messages`);
      const data = await res.json();
      setMessages(data);
      
      // Mark messages as read
      const unreadIds = data.filter(m => !m.read && m.recipient_id === 'admin').map(m => m.id);
      if (unreadIds.length > 0 && ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'read', message_ids: unreadIds }));
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchLoadOffers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/load-offers/admin`);
      const data = await res.json();
      setLoadOffers(data);
    } catch (error) {
      console.error('Failed to fetch load offers:', error);
    }
  };

  const fetchSignatureRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/signature-requests/admin`);
      const data = await res.json();
      setSignatureRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch signature requests:', error);
      setSignatureRequests([]);
    }
  };

  const fetchCarriers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carrier-list`, {
        credentials: 'include'  // Use cookies for authentication
      });
      const data = await res.json();
      
      if (res.ok && data.carriers && data.carriers.length > 0) {
        setCarriers(data.carriers);
      } else {
        // Fall back to mock carriers if no real data
        setCarriers([
          { id: 'CLT-001', company_name: 'Solo Express LLC', full_name: 'James Wilson', status: 'active' },
          { id: 'CLT-002', company_name: 'Highway Runner', full_name: 'Mike Chen', status: 'active' },
          { id: 'CLT-003', company_name: 'Atlas Logistics', full_name: 'Michael Rodriguez', status: 'active' },
          { id: 'CLT-004', company_name: 'Swift Carriers', full_name: 'Amanda Lee', status: 'active' },
          { id: 'CLT-005', company_name: 'Titan Carriers', full_name: 'Patricia Nguyen', status: 'active' }
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch carriers:', error);
      // Use mock carriers on error
      setCarriers([
        { id: 'CLT-001', company_name: 'Solo Express LLC', full_name: 'James Wilson', status: 'active' },
        { id: 'CLT-002', company_name: 'Highway Runner', full_name: 'Mike Chen', status: 'active' },
        { id: 'CLT-003', company_name: 'Atlas Logistics', full_name: 'Michael Rodriguez', status: 'active' },
        { id: 'CLT-004', company_name: 'Swift Carriers', full_name: 'Amanda Lee', status: 'active' },
        { id: 'CLT-005', company_name: 'Titan Carriers', full_name: 'Patricia Nguyen', status: 'active' }
      ]);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/unread-count/admin`);
      const data = await res.json();
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setIsTyping(false);
    setTypingUser(null);
    fetchMessages(conversation.id);
  };

  // Send typing indicator to WebSocket
  const sendTypingIndicator = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedConversation) return;
    
    const recipient = selectedConversation.participants.find(p => p.type === 'carrier');
    if (!recipient) return;
    
    // Throttle typing indicators (send max once per second)
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1000) return;
    lastTypingSentRef.current = now;
    
    ws.send(JSON.stringify({
      type: 'typing',
      conversation_id: selectedConversation.id,
      recipient_id: recipient.id
    }));
  }, [ws, selectedConversation]);

  // Handle message input change with typing indicator
  const handleMessageChange = (e) => {
    setNewMessage(e.target.value);
    if (e.target.value.trim()) {
      sendTypingIndicator();
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const recipient = selectedConversation.participants.find(p => p.type === 'carrier');
    if (!recipient) return;

    try {
      const res = await fetch(`${API_URL}/api/messaging/messages?sender_id=admin&sender_name=Queen B Admin&sender_type=admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: selectedConversation.id,
          recipient_id: recipient.id,
          content: newMessage,
          message_type: 'text'
        })
      });

      if (res.ok) {
        const message = await res.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
        fetchConversations();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const startNewConversation = async (carrier) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/messages?sender_id=admin&sender_name=Queen B Admin&sender_type=admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: carrier.id,
          content: `Hello ${carrier.company_name || carrier.full_name}! How can I help you today?`,
          message_type: 'text'
        })
      });

      if (res.ok) {
        setShowNewConversation(false);
        fetchConversations();
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter(conv => {
    const carrier = conv.participants.find(p => p.type === 'carrier');
    return carrier?.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <AdminLayout title="Messages" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Messages" subtitle="Real-Time Communication Hub">
      {/* View Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-[#0A0A0A] border border-white/5 inline-flex">
        <button
          onClick={() => setActiveView('messages')}
          className={`flex items-center gap-2 px-5 py-2.5 font-semibold text-sm tracking-wide transition-all duration-200 ${
            activeView === 'messages' 
              ? 'bg-[#D4AF37] text-black' 
              : 'text-white/50 hover:text-white hover:bg-white/5'
          }`}
          data-testid="messages-tab"
        >
          <MessageCircle className="w-4 h-4" />
          Messages
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-[10px] bg-red-500 text-white rounded-full font-bold">{unreadCount}</span>
          )}
        </button>
        <button
          onClick={() => setActiveView('load-offers')}
          className={`flex items-center gap-2 px-5 py-2.5 font-semibold text-sm tracking-wide transition-all duration-200 ${
            activeView === 'load-offers' 
              ? 'bg-[#D4AF37] text-black' 
              : 'text-white/50 hover:text-white hover:bg-white/5'
          }`}
          data-testid="load-offers-tab"
        >
          <Package className="w-4 h-4" />
          Load Offers
        </button>
        <button
          onClick={() => setActiveView('signatures')}
          className={`flex items-center gap-2 px-5 py-2.5 font-semibold text-sm tracking-wide transition-all duration-200 ${
            activeView === 'signatures' 
              ? 'bg-[#D4AF37] text-black' 
              : 'text-white/50 hover:text-white hover:bg-white/5'
          }`}
          data-testid="signatures-tab"
        >
          <FileSignature className="w-4 h-4" />
          Signatures
          {signatureRequests.filter(r => r.status === 'pending').length > 0 && (
            <span className="px-2 py-0.5 text-[10px] bg-orange-500 text-white rounded-full font-bold">
              {signatureRequests.filter(r => r.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {/* Messages View */}
      {activeView === 'messages' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-280px)]">
          {/* Conversation List */}
          <div className="lg:col-span-1 bg-[#121212] border border-white/10 flex flex-col">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold">Conversations</h3>
                <button
                  onClick={() => setShowNewConversation(true)}
                  className="p-2 bg-[#D4AF37] text-black hover:bg-[#B8962F] transition-colors"
                  data-testid="new-conversation-btn"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search carriers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageCircle className="w-10 h-10 text-white/20 mx-auto mb-2" />
                  <p className="text-white/50 text-sm">No conversations yet</p>
                </div>
              ) : (
                filteredConversations.map(conv => {
                  const carrier = conv.participants.find(p => p.type === 'carrier');
                  return (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full p-4 text-left border-b border-white/5 hover:bg-white/5 transition-colors ${
                        selectedConversation?.id === conv.id ? 'bg-white/10' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold">
                          {carrier?.name?.charAt(0) || 'C'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-white font-medium truncate">{carrier?.name || 'Unknown'}</p>
                            <span className="text-white/40 text-xs">
                              {conv.last_message?.created_at 
                                ? new Date(conv.last_message.created_at).toLocaleDateString()
                                : ''
                              }
                            </span>
                          </div>
                          <p className="text-white/50 text-sm truncate">
                            {conv.last_message?.content || 'No messages yet'}
                          </p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="px-2 py-0.5 text-xs bg-[#D4AF37] text-black rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-2 bg-[#121212] border border-white/10 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold">
                      {selectedConversation.participants.find(p => p.type === 'carrier')?.name?.charAt(0) || 'C'}
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {selectedConversation.participants.find(p => p.type === 'carrier')?.name || 'Unknown'}
                      </p>
                      <p className="text-white/40 text-xs flex items-center gap-1">
                        <Circle className="w-2 h-2 fill-green-400 text-green-400" />
                        Online
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowLoadOfferModal(true)}
                      className="px-3 py-1.5 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#B8962F] transition-colors flex items-center gap-1"
                    >
                      <Package className="w-4 h-4" />
                      Send Load
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${msg.sender_type === 'admin' ? 'order-2' : 'order-1'}`}>
                        <div className={`p-3 ${
                          msg.sender_type === 'admin' 
                            ? 'bg-[#D4AF37] text-black' 
                            : 'bg-[#0A0A0A] border border-white/10 text-white'
                        }`}>
                          {msg.message_type === 'image' && msg.attachment_url && (
                            <img src={msg.attachment_url} alt="Shared" className="max-w-full mb-2 rounded" />
                          )}
                          {msg.message_type === 'pdf' && msg.attachment_url && (
                            <a 
                              href={msg.attachment_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-black/10 mb-2"
                            >
                              <FileText className="w-5 h-5" />
                              <span className="text-sm">{msg.attachment_name || 'Document.pdf'}</span>
                            </a>
                          )}
                          <p className="text-sm">{msg.content}</p>
                        </div>
                        <div className={`flex items-center gap-1 text-xs mt-1 ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'} text-white/40`}>
                          <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {msg.sender_type === 'admin' && (
                            <span className="flex items-center" title={msg.read ? 'Read' : 'Delivered'}>
                              {msg.read ? (
                                <CheckCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                              ) : (
                                <Check className="w-3.5 h-3.5 text-white/40" />
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing Indicator */}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg">
                        <TypingIndicator />
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        // Handle file upload (mocked)
                        console.log('File selected:', e.target.files[0]);
                      }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={handleMessageChange}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      className="flex-1 bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
                      data-testid="message-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="p-2 bg-[#D4AF37] text-black hover:bg-[#B8962F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-white/10 mx-auto mb-4" />
                  <p className="text-white/50">Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Load Offers View */}
      {activeView === 'load-offers' && (
        <LoadOffersView 
          loadOffers={loadOffers} 
          carriers={carriers}
          onNewOffer={() => setShowLoadOfferModal(true)}
          onRefresh={fetchLoadOffers}
        />
      )}

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#121212] border border-white/10 w-full max-w-md">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-bold">Start New Conversation</h3>
              <button onClick={() => setShowNewConversation(false)} className="p-2 hover:bg-white/10">
                <X className="w-5 h-5 text-white/50" />
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {carriers.map(carrier => (
                <button
                  key={carrier.id}
                  onClick={() => startNewConversation(carrier)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/5 border-b border-white/5 transition-colors"
                >
                  <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold">
                    {(carrier.company_name || carrier.full_name)?.charAt(0) || 'C'}
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium">{carrier.company_name || carrier.full_name}</p>
                    <p className="text-white/40 text-xs">{carrier.id}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Load Offer Modal */}
      {showLoadOfferModal && (
        <LoadOfferModal
          carriers={carriers}
          selectedCarrier={selectedConversation?.participants.find(p => p.type === 'carrier')}
          onClose={() => setShowLoadOfferModal(false)}
          onSend={async (offer) => {
            try {
              const res = await fetch(`${API_URL}/api/messaging/load-offers?admin_id=admin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(offer)
              });
              if (res.ok) {
                setShowLoadOfferModal(false);
                fetchLoadOffers();
              }
            } catch (error) {
              console.error('Failed to send load offer:', error);
            }
          }}
        />
      )}

      {/* Signatures View */}
      {activeView === 'signatures' && (
        <SignaturesView 
          signatureRequests={signatureRequests} 
          carriers={carriers}
          onNewRequest={() => setShowSignatureModal(true)}
          onRefresh={fetchSignatureRequests}
        />
      )}

      {/* Signature Request Modal */}
      {showSignatureModal && (
        <SignatureRequestModal
          carriers={carriers}
          onClose={() => setShowSignatureModal(false)}
          onSend={async (request) => {
            try {
              const res = await fetch(`${API_URL}/api/messaging/signature-requests?admin_id=admin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(request)
              });
              if (res.ok) {
                setShowSignatureModal(false);
                fetchSignatureRequests();
              }
            } catch (error) {
              console.error('Failed to create signature request:', error);
            }
          }}
        />
      )}
    </AdminLayout>
  );
};

// Load Offers View Component
const LoadOffersView = ({ loadOffers, carriers, onNewOffer, onRefresh }) => {
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredOffers = loadOffers.filter(offer => 
    filterStatus === 'all' || offer.status === filterStatus
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400">Pending</span>;
      case 'accepted':
        return <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400">Accepted</span>;
      case 'declined':
        return <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400">Declined</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#121212] border border-white/10 px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
          </select>
        </div>
        <button
          onClick={onNewOffer}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#B8962F] transition-colors"
          data-testid="new-load-offer-btn"
        >
          <Plus className="w-4 h-4" />
          New Load Offer
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-yellow-400">{loadOffers.filter(o => o.status === 'pending').length}</p>
          <p className="text-white/50 text-xs">Pending</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-green-400">{loadOffers.filter(o => o.status === 'accepted').length}</p>
          <p className="text-white/50 text-xs">Accepted</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-red-400">{loadOffers.filter(o => o.status === 'declined').length}</p>
          <p className="text-white/50 text-xs">Declined</p>
        </div>
      </div>

      {/* Load Offers List */}
      <div className="bg-[#121212] border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-white font-bold">Load Offers</h3>
        </div>
        {filteredOffers.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50">No load offers found</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filteredOffers.map(offer => (
              <div key={offer.id} className="p-4 hover:bg-white/5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-white font-medium">{offer.carrier_name}</p>
                      {getStatusBadge(offer.status)}
                    </div>
                    <p className="text-white/70 text-sm mb-2">{offer.load_details}</p>
                    {(offer.origin || offer.destination) && (
                      <p className="text-white/50 text-sm">
                        {offer.origin} → {offer.destination}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      {offer.rate && (
                        <span className="text-[#22C55E] font-medium">${offer.rate.toLocaleString()}</span>
                      )}
                      {offer.pickup_date && (
                        <span className="text-white/40">Pickup: {offer.pickup_date}</span>
                      )}
                    </div>
                  </div>
                  {offer.screenshot_url && (
                    <img 
                      src={offer.screenshot_url} 
                      alt="Load screenshot" 
                      className="w-24 h-24 object-cover border border-white/10"
                    />
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-white/40 text-xs">
                    Sent {new Date(offer.created_at).toLocaleDateString()}
                  </span>
                  {offer.responded_at && (
                    <span className="text-white/40 text-xs">
                      Responded {new Date(offer.responded_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Load Offer Modal Component
const LoadOfferModal = ({ carriers, selectedCarrier, onClose, onSend }) => {
  const [formData, setFormData] = useState({
    carrier_id: selectedCarrier?.id || '',
    carrier_name: selectedCarrier?.name || '',
    load_details: '',
    screenshot_url: '',
    origin: '',
    destination: '',
    rate: '',
    pickup_date: '',
    delivery_date: ''
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  const handleCarrierSelect = (e) => {
    const carrier = carriers.find(c => c.id === e.target.value);
    setFormData({
      ...formData,
      carrier_id: carrier?.id || '',
      carrier_name: carrier?.company_name || carrier?.full_name || ''
    });
  };

  const handleScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Mock upload - create preview
      const reader = new FileReader();
      reader.onload = () => {
        setScreenshotPreview(reader.result);
        setFormData({ ...formData, screenshot_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (!formData.carrier_id || !formData.load_details) return;
    onSend({
      ...formData,
      rate: formData.rate ? parseFloat(formData.rate) : null
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#121212]">
          <h3 className="text-white font-bold">Send Load Offer</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10">
            <X className="w-5 h-5 text-white/50" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Carrier Selection */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Carrier *</label>
            <select
              value={formData.carrier_id}
              onChange={handleCarrierSelect}
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
            >
              <option value="">Select carrier...</option>
              {carriers.map(carrier => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.company_name || carrier.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Screenshot Upload */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Load Screenshot</label>
            <div className="border-2 border-dashed border-white/20 p-4 text-center">
              {screenshotPreview ? (
                <div className="relative">
                  <img src={screenshotPreview} alt="Preview" className="max-h-48 mx-auto" />
                  <button
                    onClick={() => {
                      setScreenshotPreview(null);
                      setFormData({ ...formData, screenshot_url: '' });
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Camera className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white text-sm">Click to upload screenshot</p>
                  <p className="text-white/40 text-xs">JPG, PNG up to 10MB</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Load Details */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Load Details *</label>
            <textarea
              value={formData.load_details}
              onChange={(e) => setFormData({ ...formData, load_details: e.target.value })}
              placeholder="Describe the load..."
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37] min-h-[100px] resize-none"
            />
          </div>

          {/* Origin & Destination */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/40 text-xs uppercase mb-1 block">Origin</label>
              <input
                type="text"
                value={formData.origin}
                onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                placeholder="City, State"
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase mb-1 block">Destination</label>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                placeholder="City, State"
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
          </div>

          {/* Rate */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Rate ($)</label>
            <input
              type="number"
              value={formData.rate}
              onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
              placeholder="0.00"
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/40 text-xs uppercase mb-1 block">Pickup Date</label>
              <input
                type="date"
                value={formData.pickup_date}
                onChange={(e) => setFormData({ ...formData, pickup_date: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase mb-1 block">Delivery Date</label>
              <input
                type="date"
                value={formData.delivery_date}
                onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white/70 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.carrier_id || !formData.load_details}
            className="px-6 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#B8962F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send Offer
          </button>
        </div>
      </div>
    </div>
  );
};

// Signatures View Component
const SignaturesView = ({ signatureRequests, carriers, onNewRequest, onRefresh }) => {
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredRequests = signatureRequests.filter(req => 
    filterStatus === 'all' || req.status === filterStatus
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-orange-500/20 text-orange-400">Awaiting Signature</span>;
      case 'signed':
        return <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400">Signed</span>;
      case 'declined':
        return <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400">Declined</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#121212] border border-white/10 px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="all">All Status</option>
            <option value="pending">Awaiting Signature</option>
            <option value="signed">Signed</option>
            <option value="declined">Declined</option>
          </select>
        </div>
        <button
          onClick={onNewRequest}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#B8962F] transition-colors"
          data-testid="new-signature-request-btn"
        >
          <Plus className="w-4 h-4" />
          Request Signature
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-orange-400">{signatureRequests.filter(r => r.status === 'pending').length}</p>
          <p className="text-white/50 text-xs">Awaiting</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-green-400">{signatureRequests.filter(r => r.status === 'signed').length}</p>
          <p className="text-white/50 text-xs">Signed</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4 text-center">
          <p className="text-3xl font-bold text-red-400">{signatureRequests.filter(r => r.status === 'declined').length}</p>
          <p className="text-white/50 text-xs">Declined</p>
        </div>
      </div>

      {/* Signature Requests List */}
      <div className="bg-[#121212] border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-white font-bold">Signature Requests</h3>
        </div>
        {filteredRequests.length === 0 ? (
          <div className="p-8 text-center">
            <FileSignature className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50">No signature requests found</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filteredRequests.map(req => (
              <div key={req.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center ${
                      req.status === 'signed' ? 'bg-green-500/20' : 
                      req.status === 'pending' ? 'bg-orange-500/20' : 'bg-red-500/20'
                    }`}>
                      {req.status === 'signed' ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : req.status === 'pending' ? (
                        <PenTool className="w-5 h-5 text-orange-400" />
                      ) : (
                        <X className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{req.document_name}</p>
                      <p className="text-white/50 text-sm">{req.carrier_name}</p>
                    </div>
                  </div>
                  {getStatusBadge(req.status)}
                </div>
                <div className="ml-13 flex items-center gap-4 text-sm text-white/40">
                  <span>Sent: {new Date(req.created_at).toLocaleDateString()}</span>
                  {req.signed_at && (
                    <span>Signed: {new Date(req.signed_at).toLocaleDateString()}</span>
                  )}
                  {req.document_url && (
                    <a
                      href={req.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[#D4AF37] hover:underline"
                    >
                      <Download className="w-3 h-3" />
                      View Document
                    </a>
                  )}
                </div>
                {req.status === 'signed' && req.signature_data && (
                  <div className="mt-3 ml-13 bg-white/5 p-3 inline-block">
                    <p className="text-white/40 text-xs uppercase mb-1">Signature</p>
                    <p className="text-xl text-white" style={{ fontFamily: 'cursive, Georgia, serif' }}>
                      {req.signature_data.name}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Signature Request Modal
const SignatureRequestModal = ({ carriers, onClose, onSend }) => {
  const [formData, setFormData] = useState({
    carrier_id: '',
    carrier_name: '',
    document_name: '',
    document_url: '',
    notes: ''
  });

  const handleCarrierSelect = (e) => {
    const carrierId = e.target.value;
    const carrier = carriers.find(c => c.id === carrierId);
    setFormData({
      ...formData,
      carrier_id: carrierId,
      carrier_name: carrier?.company_name || carrier?.full_name || ''
    });
  };

  const handleSubmit = () => {
    if (!formData.carrier_id || !formData.document_name) return;
    onSend(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-bold">Request Document Signature</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10">
            <X className="w-5 h-5 text-white/50" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Carrier Selection */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Carrier *</label>
            <select
              value={formData.carrier_id}
              onChange={handleCarrierSelect}
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white focus:outline-none focus:border-[#D4AF37]"
            >
              <option value="">Select carrier...</option>
              {carriers.map(carrier => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.company_name || carrier.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Document Name */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Document Name *</label>
            <input
              type="text"
              value={formData.document_name}
              onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
              placeholder="e.g., Carrier Agreement, Rate Confirmation"
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>

          {/* Document URL */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Document URL (PDF Link)</label>
            <input
              type="url"
              value={formData.document_url}
              onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
              placeholder="https://example.com/document.pdf"
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
            />
            <p className="text-white/30 text-xs mt-1">Link to the PDF document the carrier needs to sign</p>
          </div>

          {/* Notes */}
          <div>
            <label className="text-white/40 text-xs uppercase mb-1 block">Notes for Carrier</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any special instructions..."
              className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37] min-h-[80px] resize-none"
            />
          </div>
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white/70 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.carrier_id || !formData.document_name}
            className="px-6 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#B8962F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send Request
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMessagesSection;
